import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { APP_PATH_SIGNUP } from '../../config';
import { STATUS_ACTIVE } from '../../common/adapters';
import {
  applicationAppliedSelector,
  applicationCheckStatusSelector,
  applicationSubmitStatusSelector,
  checkCandidateApplication,
  resetSubmitApplicationStatus,
  submitCandidateApplication
} from '../../redux/features/applications/applicationsSlice';
import {
  isLoginSelector,
  userSelector
} from '../../redux/features/user/usersSlice';

const useStyles = makeStyles((theme) => ({
  applyButton: {
    width: '100%',
    padding: theme.spacing(2),
    fontSize: '1rem',
    fontWeight: 700,
    textTransform: 'none',
    background: `linear-gradient(45deg, ${theme.palette.secondary.main} 30%, ${theme.palette.secondary.light} 90%)`,
    color: theme.palette.common.white,
    boxShadow: `0 3px 12px rgba(0, 0, 0, 0.15)`,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 6px 20px rgba(0, 0, 0, 0.2)`,
      background: `linear-gradient(45deg, ${theme.palette.secondary.dark} 30%, ${theme.palette.secondary.main} 90%)`
    },
    '&:disabled': {
      background: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled
    }
  },
  applyButtonIcon: {
    fontSize: '1.5rem',
    marginRight: theme.spacing(1)
  },
  alreadyAppliedButton: {
    background: theme.palette.success.light,
    color: theme.palette.success.contrastText,
    '&:hover': {
      background: theme.palette.success.light,
      transform: 'none'
    }
  },
  dialogContent: {
    minWidth: 400
  },
  coverLetterField: {
    marginTop: theme.spacing(2)
  }
}));

const isEmail = (str) => {
  if (!str) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(str.trim());
};

const isURL = (str) => {
  if (!str) return false;
  const trimmedStr = str.trim();
  return (
    trimmedStr.startsWith('http://') ||
    trimmedStr.startsWith('https://') ||
    trimmedStr.startsWith('www.')
  );
};

const formatApplicationLink = (howToApply) => {
  if (!howToApply) return null;

  const trimmedValue = howToApply.trim();

  if (isEmail(trimmedValue)) {
    return `mailto:${trimmedValue}`;
  }

  if (isURL(trimmedValue)) {
    if (trimmedValue.startsWith('www.')) {
      return `https://${trimmedValue}`;
    }
    return trimmedValue;
  }

  if (trimmedValue.includes('@')) {
    return `mailto:${trimmedValue}`;
  }

  if (trimmedValue.includes('.')) {
    return `https://${trimmedValue}`;
  }

  return `https://${trimmedValue}`;
};

/**
 * ApplyButton component
 * Renders a button that either:
 * - Opens an external application link when internal candidates are disabled
 * - Shows an internal application dialog for candidates
 */
const ApplyButton = ({
  buttonText,
  icon,
  className,
  fullWidth,
  jobId,
  job,
  ...props
}) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const user = useSelector(userSelector);
  const isLogin = useSelector(isLoginSelector);

  const resolvedJobId = jobId || job?.id;
  const internalCandidatesEnabled = job?.internalCandidatesEnabled !== false;
  const hasExternalApply = useMemo(() => {
    return Boolean(
      !internalCandidatesEnabled && job?.howToApply && job.howToApply.trim()
    );
  }, [internalCandidatesEnabled, job]);
  const externalApplicationLink = useMemo(() => {
    if (!hasExternalApply) return null;
    return formatApplicationLink(job.howToApply);
  }, [hasExternalApply, job]);
  const isActiveJob = !job?.status || job.status === STATUS_ACTIVE;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [coverLetter, setCoverLetter] = useState('');

  const hasApplied = useSelector(applicationAppliedSelector(resolvedJobId));
  const checkStatus = useSelector(
    applicationCheckStatusSelector(resolvedJobId)
  );
  const checkingApplication = checkStatus === 'loading';
  const submitStatus = useSelector(
    applicationSubmitStatusSelector(resolvedJobId)
  );
  const isSubmitting = submitStatus === 'loading';

  useEffect(() => {
    if (
      checkStatus === 'idle' &&
      internalCandidatesEnabled &&
      resolvedJobId &&
      isLogin &&
      user?.roles?.includes('ROLE_CANDIDATE')
    ) {
      dispatch(checkCandidateApplication(resolvedJobId));
    }
  }, [
    checkStatus,
    dispatch,
    internalCandidatesEnabled,
    resolvedJobId,
    isLogin,
    user
  ]);

  useEffect(() => {
    if (submitStatus === 'succeeded') {
      setDialogOpen(false);
      setCoverLetter('');
      dispatch(resetSubmitApplicationStatus({ jobId: resolvedJobId }));
    }
  }, [dispatch, resolvedJobId, submitStatus]);

  if (!isActiveJob) {
    return (
      <Button
        className={`${classes.applyButton} ${className || ''}`}
        variant="contained"
        fullWidth={fullWidth}
        disabled
        {...props}>
        <span
          className={classes.applyButtonIcon}
          role="img"
          aria-label="rocket">
          {icon || ''}
        </span>
        Applications Closed
      </Button>
    );
  }

  if (hasExternalApply) {
    if (!externalApplicationLink) {
      return null;
    }
    const isMailto = externalApplicationLink.startsWith('mailto:');
    const handleExternalApply = (event) => {
      event.preventDefault();
      window.open(
        externalApplicationLink,
        '_blank',
        isMailto ? '' : 'noopener,noreferrer'
      );
    };

    return (
      <Button
        className={`${classes.applyButton} ${className || ''}`}
        variant="contained"
        fullWidth={fullWidth}
        onClick={handleExternalApply}
        component="a"
        href={externalApplicationLink}
        target="_blank"
        rel={isMailto ? undefined : 'noopener noreferrer'}
        {...props}>
        <span
          className={classes.applyButtonIcon}
          role="img"
          aria-label="rocket">
          {icon || ''}
        </span>
        Apply on Company Site
      </Button>
    );
  }

  if (!internalCandidatesEnabled && !hasExternalApply) {
    return null;
  }

  if (!isLogin && internalCandidatesEnabled) {
    return (
      <Button
        className={`${classes.applyButton} ${className || ''}`}
        variant="contained"
        fullWidth={fullWidth}
        onClick={() => history.push(APP_PATH_SIGNUP)}
        {...props}>
        <span
          className={classes.applyButtonIcon}
          role="img"
          aria-label="rocket">
          {icon || ''}
        </span>
        Apply Now - Join the Team!
      </Button>
    );
  }

  if (internalCandidatesEnabled && !user?.roles?.includes('ROLE_CANDIDATE')) {
    return null;
  }

  if (internalCandidatesEnabled && hasApplied) {
    return (
      <Button
        className={`${classes.applyButton} ${classes.alreadyAppliedButton} ${
          className || ''
        }`}
        variant="contained"
        fullWidth={fullWidth}
        disabled
        {...props}>
        <span className={classes.applyButtonIcon} role="img" aria-label="check">
          ✅
        </span>
        Already Applied
      </Button>
    );
  }

  const handleInternalApply = () => {
    setDialogOpen(true);
  };

  const handleSubmitApplication = () => {
    dispatch(
      submitCandidateApplication({
        jobId: resolvedJobId,
        coverLetter
      })
    );
  };

  const handleCloseDialog = () => {
    if (!isSubmitting) {
      setDialogOpen(false);
      setCoverLetter('');
    }
  };

  return (
    <>
      <Button
        className={`${classes.applyButton} ${className || ''}`}
        variant="contained"
        fullWidth={fullWidth}
        onClick={handleInternalApply}
        disabled={checkingApplication}
        {...props}>
        {checkingApplication ? (
          <>
            <CircularProgress
              size={24}
              color="inherit"
              style={{ marginRight: 8 }}
            />
            Checking...
          </>
        ) : (
          <>
            <span
              className={classes.applyButtonIcon}
              role="img"
              aria-label="rocket">
              {icon || ''}
            </span>
            {buttonText || 'Apply Now - Join the Team!'}
          </>
        )}
      </Button>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Apply for this Position</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            You're applying as a registered candidate. The company will be able
            to review your application directly through the platform.
          </Typography>
          <TextField
            className={classes.coverLetterField}
            fullWidth
            multiline
            minRows={6}
            variant="outlined"
            label="Cover Letter (Optional)"
            placeholder="Tell the company why you're a great fit for this role..."
            value={coverLetter}
            onChange={(e) => setCoverLetter(e.target.value)}
            disabled={isSubmitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitApplication}
            color="primary"
            variant="contained"
            disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <CircularProgress
                  size={20}
                  color="inherit"
                  style={{ marginRight: 8 }}
                />
                Submitting...
              </>
            ) : (
              'Submit Application'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ApplyButton.propTypes = {
  buttonText: PropTypes.string,
  icon: PropTypes.string,
  className: PropTypes.string,
  fullWidth: PropTypes.bool,
  jobId: PropTypes.string,
  job: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    howToApply: PropTypes.string,
    internalCandidatesEnabled: PropTypes.bool
  })
};

ApplyButton.defaultProps = {
  buttonText: 'Apply Now - Join the Team!',
  icon: '',
  fullWidth: true,
  jobId: null,
  job: null
};

export default ApplyButton;
